// The Warga & Properti overview: how many units there are and how they're
// lived in, who has signed up, and which occupied houses still have nobody
// registered. Plus the residents-with-their-unit spreadsheet.
// Pure functions over the units and residents lists, so they're cheap to test.

import type { AdminUnit, PublicResident } from '@residenone/shared';
import { csvFile } from './annual-export';

// The UI only tells lived-in from vacant: OWNER_ABSENT (rented out) still
// exists in the data but counts as occupied here.
const livedIn = (u: AdminUnit) => u.occupancy !== 'VACANT';

export interface BlockSummary {
  block: string;
  units: number;
  occupied: number;
  vacant: number;
  /** Active residents linked to a unit in this block. */
  residents: number;
}

export interface PeopleSummary {
  units: number;
  byType: Record<AdminUnit['type'], number>;
  occupied: number;
  vacant: number;
  residentsActive: number;
  /** Signed up but not yet linked to a unit with an invite code. */
  residentsPending: number;
  residentsSuspended: number;
  /** Units with at least one active resident. */
  unitsWithResidents: number;
  /** Lived-in units where no active resident is registered: who to invite next. */
  occupiedWithoutResident: AdminUnit[];
  blocks: BlockSummary[];
}

const unitKey = (block: string | null, number: string | null) => `${block ?? ''}|${number ?? ''}`;

const byBlockThenNumber = (a: { block: string; number: string }, b: { block: string; number: string }) =>
  a.block.localeCompare(b.block) || a.number.localeCompare(b.number, undefined, { numeric: true });

/** Active residents grouped by the unit they live in. Residents carry block + number, not a unit id. */
function activeByUnit(residents: PublicResident[]): Map<string, PublicResident[]> {
  const map = new Map<string, PublicResident[]>();
  for (const r of residents) {
    if (r.status !== 'ACTIVE' || r.unit_block === null || r.unit_number === null) continue;
    const key = unitKey(r.unit_block, r.unit_number);
    map.set(key, [...(map.get(key) ?? []), r]);
  }
  return map;
}

export function summarizePeople(units: AdminUnit[], residents: PublicResident[]): PeopleSummary {
  const living = activeByUnit(residents);
  const byType: PeopleSummary['byType'] = { HOUSE: 0, APARTMENT: 0, RUKO: 0 };
  const blocks = new Map<string, BlockSummary>();
  const occupiedWithoutResident: AdminUnit[] = [];
  let unitsWithResidents = 0;

  for (const u of units) {
    byType[u.type] += 1;
    const here = living.get(unitKey(u.block, u.number))?.length ?? 0;
    if (here > 0) unitsWithResidents += 1;
    else if (livedIn(u)) occupiedWithoutResident.push(u);

    const b = blocks.get(u.block) ?? { block: u.block, units: 0, occupied: 0, vacant: 0, residents: 0 };
    b.units += 1;
    b.residents += here;
    if (livedIn(u)) b.occupied += 1;
    else b.vacant += 1;
    blocks.set(u.block, b);
  }

  const count = (status: PublicResident['status']) => residents.filter((r) => r.status === status).length;
  return {
    units: units.length,
    byType,
    occupied: units.filter(livedIn).length,
    vacant: units.filter((u) => !livedIn(u)).length,
    residentsActive: count('ACTIVE'),
    residentsPending: count('PENDING'),
    residentsSuspended: count('SUSPENDED'),
    unitsWithResidents,
    occupiedWithoutResident: occupiedWithoutResident.sort(byBlockThenNumber),
    blocks: [...blocks.values()].sort((a, b) => a.block.localeCompare(b.block)),
  };
}

export interface SignupMonth {
  /** 'YYYY-MM' */
  month: string;
  /** Residents who registered that month (any status). */
  joined: number;
  /** Everyone registered by the end of that month. */
  total: number;
}

/** Registrations per month for the last `months` months, oldest first, ending with today's month. */
export function signupTrend(residents: PublicResident[], today: Date, months = 12): SignupMonth[] {
  const keys = Array.from({ length: months }, (_, i) =>
    new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - (months - 1 - i), 1)).toISOString().slice(0, 7),
  );
  const joinedIn = (m: string) => residents.filter((r) => r.created_at.slice(0, 7) === m).length;
  let total = residents.filter((r) => r.created_at.slice(0, 7) < keys[0]!).length;
  return keys.map((month) => {
    const joined = joinedIn(month);
    total += joined;
    return { month, joined, total };
  });
}

export interface ResidentsCsvLabels {
  block: string;
  number: string;
  unit: string;
  type: string;
  occupancy: string;
  name: string;
  email: string;
  status: string;
  primary: string;
  since: string;
  yes: string;
  no: string;
  /** Display names for the enum values. */
  types: Record<AdminUnit['type'], string>;
  occupancies: Record<AdminUnit['occupancy'], string>;
  statuses: Record<PublicResident['status'], string>;
}

/**
 * Every unit with the people registered there: one row per resident (primary
 * first), one empty-resident row for a unit nobody has registered in, and
 * residents not linked to any unit yet at the end.
 */
export function buildResidentsCsv(units: AdminUnit[], residents: PublicResident[], labels: ResidentsCsvLabels): string {
  const byUnit = new Map<string, PublicResident[]>();
  const unlinked: PublicResident[] = [];
  for (const r of residents) {
    if (r.unit_block === null || r.unit_number === null) unlinked.push(r);
    else {
      const key = unitKey(r.unit_block, r.unit_number);
      byUnit.set(key, [...(byUnit.get(key) ?? []), r]);
    }
  }

  const person = (r: PublicResident) => [
    r.full_name,
    r.email,
    labels.statuses[r.status],
    r.is_primary === null ? '' : r.is_primary ? labels.yes : labels.no,
    r.created_at.slice(0, 10),
  ];
  const noPerson = ['', '', '', '', ''];

  const rows: string[][] = [];
  for (const u of [...units].sort(byBlockThenNumber)) {
    const unitCells = [u.block, u.number, u.label, labels.types[u.type], labels.occupancies[u.occupancy]];
    const people = (byUnit.get(unitKey(u.block, u.number)) ?? []).sort(
      (a, b) => Number(b.is_primary ?? false) - Number(a.is_primary ?? false) || a.full_name.localeCompare(b.full_name),
    );
    if (people.length === 0) rows.push([...unitCells, ...noPerson]);
    for (const r of people) rows.push([...unitCells, ...person(r)]);
  }
  for (const r of unlinked.sort((a, b) => a.full_name.localeCompare(b.full_name))) {
    rows.push(['', '', '', '', '', ...person(r)]);
  }

  const header = [
    labels.block, labels.number, labels.unit, labels.type, labels.occupancy,
    labels.name, labels.email, labels.status, labels.primary, labels.since,
  ];
  return csvFile([header, ...rows]);
}
